import { useEffect, useState } from 'react';
import { usePeopleSearch } from './usePeopleSearch';
import { useProjectsSearch } from './useProjectsSearch';
import type { EntitySearchSource } from '../components/EntityFilterSelect';

const SEARCH_PAGE_SIZE = 20;
const DEBOUNCE_MS = 250;

function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/**
 * Debounced worker search — the one people-search path behind every entity picker, so the
 * desktop popover and the mobile filters sheet can never drift on page size, debounce, or
 * empty-state wording.
 */
export function useWorkerSearchSource(): EntitySearchSource {
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, DEBOUNCE_MS);
  const { data, isLoading, error } = usePeopleSearch({
    page: 1,
    pageSize: SEARCH_PAGE_SIZE,
    filter: debounced || undefined,
  });

  return {
    searchPlaceholder: 'Search workers by name…',
    term,
    onTermChange: setTerm,
    results: (data?.items ?? []).map((p) => ({ id: p.id, label: p.displayName })),
    isLoading,
    error,
    emptyState: 'No workers match your search.',
    hasMore: (data?.totalCount ?? 0) > (data?.items.length ?? 0),
  };
}

/** Debounced project search — the projects-side counterpart to {@link useWorkerSearchSource}. */
export function useProjectSearchSource(): EntitySearchSource {
  const [term, setTerm] = useState('');
  const debounced = useDebounced(term, DEBOUNCE_MS);
  const { data, isLoading, error } = useProjectsSearch({
    page: 1,
    pageSize: SEARCH_PAGE_SIZE,
    filter: debounced || undefined,
  });

  return {
    searchPlaceholder: 'Search by job number or name…',
    term,
    onTermChange: setTerm,
    results: (data?.items ?? []).map((p) => ({ id: p.id, label: `${p.jobNumber} - ${p.name}` })),
    isLoading,
    error,
    emptyState: 'No projects match your search.',
    hasMore: (data?.totalCount ?? 0) > (data?.items.length ?? 0),
  };
}
